/**
 * OPERATIONAL LINEAGE SUMMARY CARD
 *
 * Compact read-only display of lineage graph metadata.
 */

import React from 'react';
import type { OperationalLineageGraph, LineageVerificationResult } from '../operationalLineageTypes.js';

interface Props {
  graph: OperationalLineageGraph | null;
  verification: LineageVerificationResult | null;
}

export const OperationalLineageSummaryCard: React.FC<Props> = ({ graph, verification }) => {
  if (!graph) {
    return (
      <div data-testid="lineage-summary-empty">
        <p>No lineage graph available.</p>
      </div>
    );
  }

  const nodeTypeCounts = new Map<string, number>();
  for (const node of graph.nodes) {
    nodeTypeCounts.set(node.type, (nodeTypeCounts.get(node.type) ?? 0) + 1);
  }

  return (
    <div data-testid="lineage-summary-card">
      <h4>Lineage Graph Summary</h4>
      <div>
        <span>Version: </span>
        <code>{graph.graphVersion}</code>
      </div>
      <div>
        <span>Nodes: </span>
        <strong>{graph.nodeCount}</strong>
      </div>
      <div>
        <span>Edges: </span>
        <strong>{graph.edgeCount}</strong>
      </div>
      <div>
        <span>Graph Hash: </span>
        <code>{graph.graphHash.slice(0, 24)}...</code>
      </div>
      <div>
        <span>Node Types: </span>
        {Array.from(nodeTypeCounts.entries()).map(([type, count]) => (
          <span key={type} style={{ marginRight: 8 }}>
            {type}: {count}
          </span>
        ))}
      </div>
      {verification && (
        <div style={{ marginTop: 8 }}>
          <span>Verification: </span>
          <strong style={{ color: verification.valid ? 'green' : 'red' }}>
            {verification.valid ? 'VALID' : 'INVALID'}
          </strong>
          {verification.errors.length > 0 && (
            <ul>
              {verification.errors.map((e, i) => (
                <li key={i} style={{ color: 'red' }}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
